import Tableview from '../../components/commos/Tableview'
import NavigationHeader from '../../components/navigation/NavigationHeader';
import { templateService, type TemplateTableRow } from '../../services/templateService';
import { useTemplateStore } from '../../stores';
import Loading from '../../features/sso-auth/components/Loading';
import { Button } from '@ab-inbev-maz/compass';
import { useNavigate } from 'react-router-dom';
import { useTableData } from '../../hooks/useTableData';
import { useAlertStore } from '../../stores/alertStore';

const TablePage = () => {
  const { getListTemplates, deleteTemplate } = useTemplateStore();
  const showAlert = useAlertStore(state => state.showAlert);
  const navigate = useNavigate();
  const handleCreateTemplate = () => navigate('/templates');
  const handleEdit = (id: string | number) => navigate(`/templates/${id}`);

  const handleDownloadTemplates = async (selectedIds: (string | number)[]) => {
    try {
      const templateIds = selectedIds.length > 0 ? selectedIds : undefined;
      const exportResult = await templateService.exportTemplates(templateIds);

      if (!exportResult.success || !exportResult.blob) {
        showAlert({
          text: 'Error al iniciar descarga',
          message: exportResult.message || 'No se pudo iniciar la exportación',
          status: 'error',
        });
        return;
      }

      const url = URL.createObjectURL(exportResult.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'templates_export.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      showAlert({
        text: 'Descarga completada',
        message: templateIds
          ? `Se descargaron ${templateIds.length} templates seleccionados`
          : 'Se descargaron todos los templates del país',
        status: 'success',
      });
    } catch (error) {
      console.error('Error al descargar templates:', error);
      showAlert({
        text: 'Error al descargar templates',
        message: 'Ocurrió un error inesperado durante la descarga',
        status: 'error',
      });
    }
  };

  const {
    datos,
    loading,
    currentPage,
    totalCount,
    pageSize,
    searchValue,
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
    handleDelete,
    handleDownload,
    totalPages,
  } = useTableData<TemplateTableRow>({
    fetchData: getListTemplates,
    deleteItems: deleteTemplate,
    onDownloadItems: handleDownloadTemplates,
    itemType: 'Templates',
  });

  const columnas = [
    {
      title: 'Miniatura',
      key: 'image',
      render: (value: string) => <img src={value} alt="Miniatura" style={{ width: '50px', height: '50px' }} />
    },
    {
      key: 'name',
      title: 'Nombre',
    },
    {
      key: 'brand',
      title: 'Marca',
    },
    {
      key: 'format_type',
      title: 'Formato',
    },
    {
      key: 'type',
      title: 'Tipo',
    },
    {
      key: 'created_at',
      title: 'Fecha de creación',
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      key: 'actions',
      title: 'Acciones',
      render: (t: undefined, data: TemplateTableRow) => (
        <Button label='Editar' rightIcon='functionalities/edit-2' onClick={handleEdit.bind(null, data.id)} />
      ),
    },
  ];

  // Mostrar loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-sm w-full space-y-8 bg-white p-8 rounded-3xl shadow-lg">
          <Loading message="Esto solo va tomar un momento, estamos cargando los templates." />
        </div>
      </div>
    );
  }


  return (
    <>
      <NavigationHeader
        title='Librería de Templates'
        description='Desde aquí puedes consultar todos los Templates guardados y crear nuevos cuando lo necesites.'
        backLink='/'
        actions={[
          <Button
            key="create-template"
            label="Crear nuevo Template"
            rightIcon="shopping/beer"
            variant="primary"
            onClick={handleCreateTemplate}
            className="cursor-pointer"
            data-testid="action-button"
          />
        ]}
      />
      <Tableview
        title='Librería de Templates'
        datos={datos}
        columnas={columnas}
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onDownload={handleDownload}
        showDeleteButton={true}
        onDelete={handleDelete}
      />
    </>
  )
}


export default TablePage
